import {ChangeDetectionStrategy, Component, computed, inject, OnInit, signal, viewChild} from '@angular/core';
import {MatFormField, MatLabel} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {FormsModule} from "@angular/forms";
import {MatCheckbox, MatCheckboxDefaultOptions, MAT_CHECKBOX_DEFAULT_OPTIONS} from "@angular/material/checkbox";
import {MatOption, MatSelect} from "@angular/material/select";
import {
    DomainService,
    MatchingDomainInfo,
    MatchingDomainsResponse,
    NameServerGroup
} from "../../services/domain.service";
import {BusyService} from "../../services/busy.service";
import {finalize, mergeMap} from "rxjs/operators";
import {MatButton} from "@angular/material/button";
import {UtilService} from "../../services/util.service";
import {
    MatCell, MatCellDef,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderCellDef, MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef,
    MatTable,
    MatTableDataSource
} from "@angular/material/table";
import {MatPaginator, PageEvent} from "@angular/material/paginator";
import {MatTooltip} from "@angular/material/tooltip";
import {MatMenu, MatMenuItem, MatMenuTrigger} from "@angular/material/menu";
import {Router} from "@angular/router";

@Component({
    selector: 'app-domain-search',
    imports: [
        MatFormField,
        MatLabel,
        MatInput,
        FormsModule,
        MatCheckbox,
        MatSelect,
        MatOption,
        MatButton,
        MatTable,
        MatPaginator,
        MatColumnDef,
        MatHeaderCell,
        MatCell,
        MatHeaderCellDef,
        MatCellDef,
        MatHeaderRow,
        MatHeaderRowDef,
        MatRow,
        MatRowDef,
        MatTooltip,
        MatMenu,
        MatMenuItem,
        MatMenuTrigger
    ],
    providers: [
        { provide: MAT_CHECKBOX_DEFAULT_OPTIONS, useValue: { clickAction: 'noop' } as MatCheckboxDefaultOptions }
    ],
    templateUrl: './domain-search.component.html',
    styleUrl: './domain-search.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DomainSearchComponent implements OnInit {

    readonly PAGE_SIZE_OPTIONS = [5, 10, 20];
    readonly DEFAULT_PAGE_SIZE = this.PAGE_SIZE_OPTIONS[1];

    readonly columnsToDisplay = ['name', 'removed', 'expired', 'status', 'ns_group', 'flags'];

    readonly eppStatuses = ['ok', 'clientDeleteProhibited', 'clientTransferProhibited', 'clientUpdateProhibited',
        'serverHold', 'serverDeleteProhibited', 'serverTransferProhibited', 'serverUpdateProhibited', 'pendingDelete'];

    private domainService = inject(DomainService);
    private busyService = inject(BusyService);
    private utilService = inject(UtilService);
    private router = inject(Router);

    partialName = signal<string>('');

    removedChecked = signal<boolean>(false);
    removedIndeterminate = signal<boolean>(false);
    removedTooltip = computed(() => {
        if (this.removedIndeterminate())
            return "Searching for both removed and non-removed domains."
        return this.removedChecked()? "Searching for only removed domains." : "Searching for only non-removed domains."
    });
    inRegistry = computed(() => this.removedIndeterminate()? null : !this.removedChecked())

    expiredChecked = signal<boolean>(false);
    expiredIndeterminate = signal<boolean>(false);
    expiredTooltip = computed(() => {
        if (this.expiredIndeterminate())
            return "Searching for both expired and non-expired domains."
        return this.expiredChecked()? "Searching for only expired domains." : "Searching for only non-expired domains."
    });
    expired = computed(() => this.expiredIndeterminate()? null : this.expiredChecked())

    nsGroups = signal<NameServerGroup[]>([]);
    selectedNsGroup = signal<string|null>(null);

    flags = signal<String[]>([]);
    selectedFlag = signal<string|null>(null);

    selectedEppStatus = signal<string|null>(null);

    paginator = viewChild<MatPaginator>('paginator');
    pageIndex = signal<number>(0);
    pageSize = signal<number>(this.DEFAULT_PAGE_SIZE);
    pageSizeOptions = signal<number[]>(this.PAGE_SIZE_OPTIONS);
    totalMatches = signal<number>(0);
    matchingDomains = signal(new MatTableDataSource<MatchingDomainInfo>([]));

    hasSearched = signal<boolean>(false);
    busy = computed(() => this.busyService.isBusy());


    ngOnInit() {
        this.busyService.showBusy();
        this.domainService.getAllFlags().pipe(
            mergeMap(flags => {
                this.flags.set(flags);
                return this.utilService.getGroups()
            }),
            finalize(() => { this.busyService.showNotBusy() })
        ).subscribe( {
            next: (groups) => {
                this.nsGroups.set(groups);
            },
            error: this.utilService.getErrorHandler("Error retrieving flags or name server groups")
        });
    }

    cycleRemovedState() {
        if (this.removedIndeterminate()) {
            this.removedChecked.set(true);
            this.removedIndeterminate.set(false);
        } else if (this.removedChecked()) {
            this.removedChecked.set(false);
            this.removedIndeterminate.set(false);
        } else {
            this.removedChecked.set(false);
            this.removedIndeterminate.set(true);
        }
    }

    cycleExpiredState() {
        if (this.expiredIndeterminate()) {
            this.expiredChecked.set(true);
            this.expiredIndeterminate.set(false);
        } else if (this.expiredChecked()) {
            this.expiredChecked.set(false);
            this.expiredIndeterminate.set(false);
        } else {
            this.expiredChecked.set(false);
            this.expiredIndeterminate.set(true);
        }
    }

    searchDomains() {
        this.totalMatches.set(0);
        this.matchingDomains.set(new MatTableDataSource<MatchingDomainInfo>([]));
        this.getMatchingDomains(0, this.pageSize());
    }

    private getMatchingDomains(pageIndex?: number, pageSize?: number) {
        this.busyService.showBusy();
        this.domainService.getMatchingDomains(this.partialName(), this.inRegistry(), this.expired(),
                                              this.selectedEppStatus(), this.selectedNsGroup(), this.selectedFlag(),
                                              pageIndex, pageSize).pipe(
             finalize(() => { this.busyService.showNotBusy() })
        ).subscribe({
            next: (result: MatchingDomainsResponse) => {
                this.hasSearched.set(true);
                this.totalMatches.set(result.total_count);
                this.matchingDomains.set(new MatTableDataSource<MatchingDomainInfo>(result.domains));
            },
            error: this.utilService.getErrorHandler("Error retrieving matching domains")
        })
    }

    onPageChange(event: PageEvent): void {
        this.getMatchingDomains(event.pageIndex, event.pageSize);
    }

    clearSearch() {
        this.partialName.set('');
        this.removedChecked.set(false);
        this.removedIndeterminate.set(true);
        this.expiredChecked.set(false);
        this.expiredIndeterminate.set(true);
        this.selectedEppStatus.set(null);
        this.selectedNsGroup.set(null);
        this.selectedFlag.set(null);
        this.totalMatches.set(0);
        this.matchingDomains.set(new MatTableDataSource<MatchingDomainInfo>([]));
    }

    goToDomain(domainName: string) {
        this.router.navigateByUrl(`/domains/${domainName}`).then();
    }

    exportTxt() {
        this.exportResults('txt');
    }

    exportCsvNoHeaders() {
        this.exportResults('csv-no-headers');
    }

    exportCsvWithHeaders() {
        this.exportResults('csv-with-headers');
    }

    private exportResults(kind: 'txt' | 'csv-no-headers' | 'csv-with-headers') {
        const total = this.totalMatches();
        if (total <= 0)
            return;
        this.busyService.showBusy();
        this.domainService.getMatchingDomains(this.partialName(), this.inRegistry(), this.expired(),
                                              this.selectedEppStatus(), this.selectedNsGroup(), this.selectedFlag(),
                                              0, total).pipe(
            finalize(() => { this.busyService.showNotBusy() })
        ).subscribe({
            next: (result: MatchingDomainsResponse) => {
                const domains = result.domains;
                const timestamp = this.fileTimestamp();
                if (kind === 'txt') {
                    const content = domains.map(d => d.name).join('\n');
                    this.downloadFile(content, `domain-search-${timestamp}.txt`, 'text/plain');
                } else {
                    const content = this.buildCsv(domains, kind === 'csv-with-headers');
                    this.downloadFile(content, `domain-search-${timestamp}.csv`, 'text/csv');
                }
            },
            error: this.utilService.getErrorHandler("Error retrieving matching domains for export")
        });
    }

    private buildCsv(domains: MatchingDomainInfo[], withHeaders: boolean): string {
        // Each search tri-state independently determines whether its column is
        // present: when the search was restricted to in-registry domains the
        // Removed column is always empty, and when restricted to non-expired
        // domains the Expired column is always empty, so we omit them.
        const includeRemoved = this.inRegistry() !== true;
        const includeExpired = this.expired() !== false;

        const lines: string[] = [];
        if (withHeaders) {
            const headers = ['Domain name'];
            if (includeRemoved)
                headers.push('Removed');
            if (includeExpired)
                headers.push('Expired');
            headers.push('EPP status', 'NS group', 'Flags');
            lines.push(headers.map(h => this.csvEscape(h)).join(','));
        }

        for (const d of domains) {
            const fields = [d.name];
            if (includeRemoved)
                fields.push(String(!d.in_registry));
            if (includeExpired)
                fields.push(String(d.expired));
            fields.push(d.status.join(' '), d.ns_group, d.flags.join(' '));
            lines.push(fields.map(f => this.csvEscape(f)).join(','));
        }

        return lines.join('\n');
    }

    private csvEscape(value: string): string {
        if (/[",\n\r]/.test(value))
            return '"' + value.replace(/"/g, '""') + '"';
        return value;
    }

    private fileTimestamp(): string {
        const now = new Date();
        const pad = (n: number) => String(n).padStart(2, '0');
        return `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`
            + `-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
    }

    private downloadFile(content: string, filename: string, mimeType: string) {
        const blob = new Blob([content], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = filename;
        anchor.click();
        URL.revokeObjectURL(url);
    }
}
